import { type TFunction } from "i18next";
import { NO_PRODUCT_FILTER } from "utils";

import { type FilterGroupConfig } from "src/components/filterConfig";

export const athleteFilterIds = {
  labels: "labels",
  partnershipTypes: "partnershipTypes",
  lastWorkoutDate: "lastWorkoutDate",
  assignment: "assignment",
  products: "products",
} as const;

interface CreateAthleteFilterConfigParams {
  t: TFunction;
  labels: { id: string; name: string; color: string }[];
  products?: { id: string; name: string }[];
  includeAssignment?: boolean;
  includeLastWorkoutDate?: boolean;
}

export function createAthleteFilterConfig(
  params: CreateAthleteFilterConfigParams,
): FilterGroupConfig[] {
  const config: FilterGroupConfig[] = [
    {
      filter: athleteFilterIds.labels,
      label: params.t("contentHub.editor.assignments.labels"),
      icon: "tabler-tag",
      selection: "multiple",
      search: {
        placeholder: params.t("labelPicker.filterLabels"),
        emptyMessage: params.t("labelPicker.empty"),
      },
      options: params.labels.map((label) => {
        return {
          value: label.id,
          label: label.name,
          color: label.color,
        };
      }),
    },
    {
      filter: athleteFilterIds.partnershipTypes,
      label: params.t("athletesPage.partnershipType"),
      icon: "tabler-users",
      selection: "multiple",
      options: [
        {
          value: "ONLINE",
          label: params.t("partnershipType.ONLINE"),
        },
        {
          value: "IN_PERSON",
          label: params.t("partnershipType.IN_PERSON"),
        },
        {
          value: "HYBRID",
          label: params.t("partnershipType.HYBRID"),
        },
      ],
    },
  ];

  if (params.products) {
    config.push({
      filter: athleteFilterIds.products,
      label: params.t("athletesPage.productFilter"),
      icon: "tabler-shopping-bag",
      selection: "multiple",
      emptyOption: {
        value: NO_PRODUCT_FILTER,
        label: params.t("athletesPage.noProduct"),
      },
      options: params.products.map((product) => {
        return { value: product.id, label: product.name };
      }),
    });
  }

  if (params.includeLastWorkoutDate) {
    config.push({
      filter: athleteFilterIds.lastWorkoutDate,
      label: params.t("athletesPage.filters.lastWorkoutDate"),
      icon: "tabler-calendar-event",
      selection: "single",
      options: [
        {
          value: "0:7",
          label: params.t("athletesPage.filters.nextDays", { count: 7 }),
        },
        {
          value: "0:14",
          label: params.t("athletesPage.filters.nextDays", { count: 14 }),
        },
      ],
      range: {
        fromLabel: params.t("athletesPage.filters.fromDays"),
        toLabel: params.t("athletesPage.filters.toDays"),
        applyLabel: params.t("athletesPage.filters.applyRange"),
        min: 0,
        max: 3650,
        initialFrom: 0,
        initialTo: 7,
      },
    });
  }

  if (params.includeAssignment) {
    config.push({
      filter: athleteFilterIds.assignment,
      label: params.t("contentHub.editor.assignments.statusColumn"),
      icon: "tabler-user",
      selection: "single",
      defaultValue: "ALL",
      options: [
        {
          value: "ALL",
          label: params.t("contentHub.editor.assignments.allStatuses"),
        },
        {
          value: "ASSIGNED",
          label: params.t("contentHub.editor.assignments.assigned"),
        },
        {
          value: "UNASSIGNED",
          label: params.t("contentHub.editor.assignments.notAssigned"),
        },
      ],
    });
  }

  return config;
}
